import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const SurveyTab = ({ module, title, description }) => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSurveys();
  }, [module]);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API_URL}/surveys/surveys?module=${module}`, {
        headers: getAuthHeaders()
      });
      setSurveys(response.data.surveys);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to fetch surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeSurvey = (survey) => {
    navigate(`/survey/${survey._id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const availableSurveys = surveys.filter(survey => survey.isActive);

    return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      
      {description && (
        <Typography variant="body1" color="text.secondary" mb={3}>
          {description}
        </Typography>
      )}
      
      {availableSurveys.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No surveys available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later for new surveys
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {availableSurveys.map((survey) => (
            <Grid item xs={12} md={6} key={survey._id}>
              <Card sx={{ 
                borderRadius: 2, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, color: '#2c3e50' }}>
                      {survey.title}
                    </Typography>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Available"
                      color="success"
                      size="small"
                      sx={{ 
                        backgroundColor: '#27ae60',
                        color: 'white',
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                          color: 'white'
                        }
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" mb={2} sx={{ lineHeight: 1.6 }}>
                    {survey.description || 'No description available'}
                  </Typography>

                  <Box display="flex" gap={1} mb={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      Questions: {survey.questions?.length || 0}
                    </Typography>
                    {(() => {
                      const sections = new Set(survey.questions?.map(q => q.section || 'General') || []);
                      if (sections.size > 1) {
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            Sections: {sections.size}
                          </Typography>
                        );
                      }
                      return null;
                    })()}
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleTakeSurvey(survey)}
                    startIcon={<AssessmentIcon />}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Take Survey
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SurveyTab; 